import {
  ComboBox,
  DefaultButton,
  IComboBoxOption,
  PrimaryButton,
} from '@fluentui/react';
import React, {
  CSSProperties,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {isMobile} from '../common/device';
import {Dialog} from '../common/Dialog';
import {
  formatBusinessDate,
  formatBusinessDateForHuman,
  formatDate,
  formatDateUTC,
  pad,
  parseBusinessDate,
  toDate,
} from '../dates/dates';
import {ResourceDetails} from '../types/ResourceDetails';
import {CalendarWidget} from './CalendarWidget';
import {ClearButton} from './common/ClearButton';
import {DateSelector} from './common/DateSelector';
import {useTimeRange} from './TimeRangeWidget';

type InputProps = {
  value: any;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  style?: CSSProperties;
  businessDayStartTimeMinutes?: number;
};

export function TimeInputWidget(props: InputProps): JSX.Element | null {
  const date = newDate(props.value);
  const [dateStr, timeStr] = formatBusinessDate(
    date,
    props.businessDayStartTimeMinutes,
  );

  const onChange = props.onChange;

  const times = useMemo(() => {
    return buildTimeOptions(props.businessDayStartTimeMinutes);
  }, [props.businessDayStartTimeMinutes]);

  const times0 = times[0].text;

  const onChangeDate = useCallback(
    (date?: Date | null) => {
      const str = formatDate(date);
      const d = parseBusinessDate(
        str,
        timeStr || times0,
        props.businessDayStartTimeMinutes,
      );
      onChange(formatDateUTC(d));
    },
    [onChange, timeStr, times0, props.businessDayStartTimeMinutes],
  );

  const onChangeTime = useCallback(
    (_: any, opt?: IComboBoxOption, index?: number, value?: string) => {
      if (opt) {
        const d = parseBusinessDate(
          dateStr || formatDate(new Date()),
          opt.text,
          props.businessDayStartTimeMinutes,
        );
        onChange(formatDateUTC(d));
        return;
      }

      if (value) {
        const d = parseBusinessDate(
          dateStr || formatDate(new Date()),
          formatTimeOrDefault(value, timeStr || times0),
          props.businessDayStartTimeMinutes,
        );
        onChange(formatDateUTC(d));
        return;
      }

      onChange('');
    },
    [onChange, dateStr, timeStr, times0, props.businessDayStartTimeMinutes],
  );

  return (
    <Container style={props.style}>
      <DateContainer>
        <DateSelector
          value={toDate(dateStr)}
          onSelectDate={onChangeDate}
          placeholder={props.placeholder}
        />
      </DateContainer>
      <TimeContainer>
        <ComboBox
          allowFreeform={true}
          autoComplete={'on'}
          options={times}
          styles={{
            optionsContainer: {
              width: '80px',
              maxHeight: '300px',
            },
          }}
          text={timeStr}
          onChange={onChangeTime}
        />
      </TimeContainer>
      <ClearButton
        onClick={() => {
          onChange('');
        }}
        styles={{root: {marginLeft: '0.5rem'}}}
      />
    </Container>
  );
}

type RangeInputProps = {
  placeholder?: string;
  readOnly?: boolean;
  calendar?: boolean;
  style?: CSSProperties;
  startId: string;
  endId: string;
  startValue: string;
  endValue: string;
  onChangeStart: (v: string) => void;
  onChangeEnd: (v: string) => void;
  onChangeRange: (start: string, end: string) => void;
  onListEvents: () => Promise<ResourceDetails[]>;
  businessDayStartHour?: number;
};

export function TimeRangeInputWidget(
  props: RangeInputProps,
): JSX.Element | null {
  return (
    <Container>
      <div>
        <TimeInputWidget
          {...props}
          style={{marginBottom: '0.5rem'}}
          value={props.startValue}
          onChange={props.onChangeStart}
        />
        <TimeInputWidget
          {...props}
          value={props.endValue}
          onChange={props.onChangeEnd}
        />
      </div>
      {renderCalendarButton(props)}
    </Container>
  );
}

type OutputProps = {
  value: any;
  style?: CSSProperties;
  businessDayStartTimeMinutes?: number;
};

export function TimeOutputWidget(props: OutputProps): JSX.Element | null {
  const date = newDate(props.value);
  const [d, t] = formatBusinessDateForHuman(
    date,
    props.businessDayStartTimeMinutes,
  );

  return (
    <Container title={`${d} ${t}`} style={props.style}>
      <DateText>{d}</DateText>
      <TimeText>{t}</TimeText>
    </Container>
  );
}

function newDate(v: any): Date | null {
  if (!v) {
    return null;
  }

  return new Date(v);
}

function formatTimeOrDefault(v: string, def: string): string {
  if (/^\d{1,2}:\d{1,2}$/.test(v)) {
    return formatTime(v);
  }

  return def;
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
}

function renderCalendarButton(props: RangeInputProps) {
  if (isMobile()) {
    // Calendar widgets are not supported on mobile at this time.
    return null;
  }

  if (!props.calendar) {
    return null;
  }

  return <CalendarButton {...props} />;
}

function CalendarButton(props: RangeInputProps): JSX.Element | null {
  const intl = useIntl();
  const dialog = useRef<Dialog>(null);
  const [start, end, setTimeRange] = useTimeRange();
  const onChange = props.onChangeRange;

  const onSelectTimeRange = useCallback(async () => {
    const s = formatDateUTC(start);
    const e = formatDateUTC(end);
    await onChange(s, e);
    dialog.current?.closeDialog();
  }, [start, end, onChange]);

  const [events, setEvents] = useState<ResourceDetails[]>([]);
  const onListEvents = props.onListEvents;

  return (
    <>
      <DefaultButton
        text={intl.formatMessage({
          id: 'Action.OpenSelector',
          defaultMessage: 'Select',
        })}
        onClick={() => {
          setTimeout(async () => {
            try {
              const list = await onListEvents();
              setEvents(list);
            } catch (e) {
              console.error(e);
            }
          }, 33);
          dialog.current?.showDialog();
        }}
        styles={{
          root: {
            marginLeft: '0.5rem',
            whiteSpace: 'nowrap',
          },
        }}
      />
      <Dialog ref={dialog} maxWidth="100%">
        <CalendarContainer>
          <CalendarWidget
            {...props}
            onSelectTimeRange={setTimeRange}
            events={events}
            value={{start, end}}
            startAccessor={props.startId}
            endAccessor={props.endId}
          />
          <Buttons>
            <PrimaryButton
              text={intl.formatMessage({id: 'Action.Decide'})}
              onClick={onSelectTimeRange}
              styles={{
                root: {
                  marginRight: '1rem',
                  whiteSpace: 'nowrap',
                },
              }}
            />
            <DefaultButton
              text={intl.formatMessage({id: 'Action.Cancel'})}
              onClick={() => {
                dialog.current?.closeDialog();
              }}
              styles={{
                root: {
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Buttons>
        </CalendarContainer>
      </Dialog>
    </>
  );
}

type TimeOption = {
  key: string;
  text: string;
};

/**
 * Generate time slots from 00:00 (+startTimeMinutes) to 23:30 (+startTimeMinutes) in increments of step minutes.
 */
function buildTimeOptions(startTimeMinutes = 0, step = 30): TimeOption[] {
  const start = startTimeMinutes;
  const end = start + 24 * 60;
  const seq: number[] = [];

  const pushRange = (start: number, end: number) => {
    for (let m = start; m < end; m += step) {
      seq.push(m);
    }
  };

  const toString = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${pad(h)}:${pad(m)}`;
  };

  pushRange(start, end);

  return seq.map((m) => {
    const str = toString(m);

    return {
      key: str,
      text: str,
    };
  });
}

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const DateText = styled.div`
  font-size: 1rem;
  margin-top: 0.2rem;
  margin-bottom: 0.2rem;
`;

const TimeText = styled(DateText)`
  margin-left: 0.5rem;
`;

const DateContainer = styled.div`
  max-width: 300px;
  margin-right: 0.5rem;
`;

const TimeContainer = styled.div`
  width: 100px;
  flex-shrink: 0;
`;

const Buttons = styled.div`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CalendarContainer = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
`;
